import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { useCart } from "@/context/CartContext";
import { api, money } from "@/lib/api";

const SHIPPING_EST = 5.0;

export default function Checkout() {
  const { items, subtotal, discount, discountCode } = useCart();
  const [email, setEmail] = useState(() => localStorage.getItem("dr_customer_email") || "");
  const [loading, setLoading] = useState(false);

  const estTax = (subtotal - discount) * 0.08;
  const total = subtotal - discount + SHIPPING_EST + estTax;

  const pay = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const { checkout_url } = await api.checkout({
        items: items.map((i) => ({ slug: i.slug, size: i.size, qty: i.qty })),
        origin_url: window.location.origin,
        email: email || undefined,
        discount_code: discountCode || undefined,
      });
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed. Try again.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="checkout-page">
      <PageHeader overline="SECURE CHECKOUT" title="Checkout" sub="No account needed. Shipping address, contact details and payment are collected on the encrypted payment page." />
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 py-14">
        {items.length === 0 ? (
          <div className="border border-wire px-8 py-24 text-center" data-testid="checkout-empty">
            <p className="font-display text-2xl font-extrabold uppercase tracking-tight text-neutral-500">Nothing to check out</p>
            <Link to="/shop" data-testid="checkout-empty-shop" className="mt-8 inline-block bg-white px-10 py-4 font-mono text-[11px] tracking-[0.35em] text-black hover:bg-neutral-300">
              SHOP NOW
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">YOUR ORDER</p>
              <div className="mt-4 border-t border-wire">
                {items.map((item) => (
                  <div key={`${item.slug}-${item.size}`} data-testid={`checkout-item-${item.size}`} className="flex items-center gap-5 border-b border-wire py-5">
                    <div className="h-20 w-16 shrink-0 overflow-hidden border border-wire bg-charcoal">
                      <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <p className="font-display text-sm font-bold uppercase tracking-wide text-white">{item.name}</p>
                      <p className="mt-1 font-mono text-[10px] tracking-[0.25em] text-neutral-500">SIZE {item.size} — QTY {item.qty}</p>
                    </div>
                    <p className="font-mono text-sm text-neutral-100">{money(item.price * item.qty)}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">EMAIL FOR ORDER UPDATES (OPTIONAL)</p>
                <input
                  data-testid="checkout-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="EMAIL ADDRESS"
                  className="mt-4 w-full border border-wire bg-transparent px-5 py-4 font-mono text-xs tracking-[0.2em] text-white placeholder:text-neutral-600 focus:border-white focus:outline-none"
                />
              </div>

              <div className="mt-10 border border-wire p-6" data-testid="checkout-steps-note">
                <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">WHAT HAPPENS NEXT</p>
                <ol className="mt-4 space-y-3 font-mono text-[11px] leading-relaxed tracking-wider text-neutral-400">
                  <li><span className="text-neutral-600">01 /</span> Enter shipping address & contact details</li>
                  <li><span className="text-neutral-600">02 /</span> Choose standard or express shipping</li>
                  <li><span className="text-neutral-600">03 /</span> Pay securely — card, Apple Pay, Google Pay or PayPal</li>
                  <li><span className="text-neutral-600">04 /</span> Order confirmed instantly with your order number</li>
                </ol>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="border border-wire bg-charcoal/50 p-8" data-testid="checkout-summary">
                <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">SUMMARY</p>
                <div className="mt-6 space-y-3 font-mono text-xs tracking-[0.2em]">
                  <div className="flex justify-between text-neutral-400">
                    <span>SUBTOTAL</span><span data-testid="checkout-subtotal" className="text-neutral-100">{money(subtotal)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-neutral-400">
                      <span>DISCOUNT (DR10)</span><span className="text-neutral-100">-{money(discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-neutral-400">
                    <span>SHIPPING FROM</span><span className="text-neutral-100">{money(SHIPPING_EST)}</span>
                  </div>
                  <div className="flex justify-between text-neutral-400">
                    <span>ESTIMATED TAX</span><span className="text-neutral-100">{money(estTax)}</span>
                  </div>
                  <div className="flex justify-between border-t border-wire pt-4 text-sm text-white">
                    <span>ESTIMATED TOTAL</span><span data-testid="checkout-total">{money(total)}</span>
                  </div>
                </div>

                <button
                  data-testid="pay-button"
                  onClick={pay}
                  disabled={loading}
                  className="mt-8 flex w-full items-center justify-center gap-3 bg-white py-5 font-mono text-xs tracking-[0.35em] text-black transition-colors hover:bg-neutral-300 disabled:opacity-60"
                >
                  {loading ? "REDIRECTING..." : <>PAY {money(total)} <ArrowRight size={14} /></>}
                </button>
                <p className="mt-4 flex items-center justify-center gap-2 font-mono text-[9px] tracking-[0.2em] text-neutral-500">
                  <Lock size={10} /> 256-BIT ENCRYPTED — FINAL TOTAL CONFIRMED AT PAYMENT
                </p>
                <p className="mt-3 text-center font-mono text-[9px] tracking-[0.25em] text-neutral-600" data-testid="payment-methods-note">
                  CARD · APPLE PAY · GOOGLE PAY · PAYPAL
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
